import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import {
    Controller,
    FormProvider,
    SubmitErrorHandler,
    SubmitHandler,
    useFieldArray,
    useForm,
} from "react-hook-form";
import { useLoaderData, useLocation, useParams } from "react-router";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { store } from "../..";
import FileUpload from "../../components/FileUpload";
import { alertActions } from "../../features/alert/alertSlice";
import {
    globalActions,
    selectAgeLevels,
} from "../../features/global/globalSlice";
import {
    leagueManagementActions,
    selectDesignations,
    selectDirections,
    selectSelectedLeague,
} from "../../features/leaguemgmt/leagueManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    createLeague,
    getBracketTypes,
    getSeasons,
    getTeamsByAgeLevel,
    updateLeague,
} from "../../lib/api";
import { LEAGUE_LOGO_URL, VALID_FILE_EXTENSIONS } from "../../lib/globals";
import { displayFormAlert } from "../../lib/utils";
import {
    IAPIResponse,
    IAgeLevel,
    IPlayoffBracket,
    ISeason,
    ITeam,
} from "../../types/api";
import { ILeagueMgmtLeagueInfoData } from "../../types/forms";
import { ILocalStorageUserData } from "../../types/users";

export const loader = async ({ params }: any) => {
    const { leagueId } = params;

    const leagues = store.getState().leaguemanagement.leagues;
    const designations = store.getState().leaguemanagement.designations;
    const directions = store.getState().leaguemanagement.directions;
    const ageLevels = store.getState().global.ageLevels;

    const leaguesPromise = leagues.length
        ? Promise.resolve()
        : store.dispatch(leagueManagementActions.getLeagues());

    const designationsPromise = designations.length
        ? Promise.resolve()
        : store.dispatch(leagueManagementActions.getDesignations());

    const directionsPromise = directions.length
        ? Promise.resolve()
        : store.dispatch(leagueManagementActions.getDirections());

    const ageLevelsPromise = ageLevels.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getAgeLevels());

    const [seasons, brackets] = await Promise.all([
        getSeasons(),
        getBracketTypes(),
        leaguesPromise,
        ageLevelsPromise,
        designationsPromise,
        directionsPromise,
    ]);

    leagueId &&
        store.dispatch(
            leagueManagementActions.setSelectedLeague(parseInt(leagueId)),
        );

    const league = leagueId
        ? selectSelectedLeague(store.getState())
        : undefined;

    return {
        leagueId,
        seasons,
        brackets,
        teams: league
            ? await getTeamsByAgeLevel(league?.ageLevel as string, league?.year)
            : undefined,
        header: league
            ? `${league?.ageLevel}${
                  league?.designation ? ` ${league.designation}` : ""
              }${league?.direction ? ` ${league.direction}` : ""}`
            : "Add League",
    };
};

const League = (): React.JSX.Element => {
    const location = useLocation();
    const { leagueId } = useParams() as any;
    const user = useAppSelector(
        state => state.auth.user,
    ) as ILocalStorageUserData["user"];

    const isCreatingLeague = /league-management\/league\/add\/?$/.test(
        location.pathname,
    );
    const dispatch = useAppDispatch();

    const ageLevels = useAppSelector(selectAgeLevels);
    const designations = useAppSelector(selectDesignations);
    const directions = useAppSelector(selectDirections);

    const [fileList, setFileList] = useState<File[]>([]);

    const league = isCreatingLeague
        ? undefined
        : useAppSelector(selectSelectedLeague);

    const [champRounds, setChampRounds] = useState<number | undefined>(
        league?.playoffBracket?.rounds.championship,
    );
    const [consRounds, setConsRounds] = useState<number | undefined>(
        league?.playoffBracket?.rounds.consolation,
    );

    const allowedExtensions = VALID_FILE_EXTENSIONS.image.slice();

    const {
        seasons,
        teams: loaderTeams,
        brackets,
    } = useLoaderData() as {
        seasons: IAPIResponse & {
            seasons: ISeason[];
        };
        teams:
            | (IAPIResponse & {
                  teams: ITeam[];
              })
            | undefined;
        brackets: IAPIResponse & {
            brackets: IPlayoffBracket[];
        };
    };

    const [teams, setTeams] = useState<
        | {
              value: number;
              label: string;
          }[]
        | undefined
    >(
        loaderTeams?.teams
            .sort((a, b) => {
                return a.name.localeCompare(b.name);
            })
            .map(t => {
                return {
                    value: t.id,
                    label: t.name,
                };
            }),
    );

    const leagueFormSchema = yup.object().shape({
        active: yup.boolean().required(),
        year: yup
            .mixed<number>()
            .required()
            .test("is-number", "Please set the league year", value => {
                if (value.toString() === "") {
                    return false;
                }
                return !isNaN(value);
            }),
        files: yup.mixed<File[]>(),
        bracket: yup.object().nullable().optional().shape({
            value: yup.number(),
            label: yup.string(),
        }),
        ageLevel: yup.object().shape({
            value: yup.number().required(),
            label: yup.string().required("Please select the league age level"),
        }),
        designation: yup
            .object()
            .shape({
                value: yup.number(),
                label: yup.string(),
            })
            .optional(),
        season: yup.object().shape({
            value: yup.number().required(),
            label: yup.string().required("Please select the league season"),
        }),
        teams: yup.array(
            yup.object().shape({
                value: yup.number(),
                label: yup.string(),
            }),
        ),
        excludedTeams: yup
            .array(
                yup.object().shape({
                    value: yup.number().required(),
                    label: yup.string().required(),
                }),
            )
            .optional(),
        playoffDates: yup.object().shape({
            championship: yup.array(
                yup.object().shape({
                    round: yup.number().nullable(),
                    date: yup.date().nullable(),
                }),
            ),
            consolation: yup.array(
                yup.object().shape({
                    round: yup.number().nullable(),
                    date: yup.date().nullable(),
                }),
            ),
        }),
    });

    const methods = useForm<ILeagueMgmtLeagueInfoData>({
        mode: "onChange",
        resolver: yupResolver(leagueFormSchema),
        defaultValues: {
            playoffDates: {
                championship: league?.playoffBracket?.dates?.championship
                    ?.length
                    ? league.playoffBracket.dates.championship.map(d => ({
                          round: parseInt(d.round.toString()),
                          date: d.date
                              ? new Date(`${d.date}T00:00:00`)
                              : undefined,
                      }))
                    : champRounds
                      ? Array.from({ length: champRounds }, (_, i) => ({
                            round: i + 1,
                            date: null,
                        }))
                      : [],
                consolation: league?.playoffBracket?.dates?.consolation?.length
                    ? league.playoffBracket.dates.consolation.map(d => ({
                          round: parseInt(d.round.toString()),
                          date: d.date
                              ? new Date(`${d.date}T00:00:00`)
                              : undefined,
                      }))
                    : consRounds
                      ? Array.from({ length: consRounds }, (_, i) => ({
                            round: i + 1,
                            date: null,
                        }))
                      : [],
            },
        },
    });

    const {
        fields: champFields,
        remove: champRemove,
        append: champAppend,
    } = useFieldArray({
        control: methods.control,
        name: `playoffDates.championship`,
    });

    const {
        fields: consFields,
        remove: consRemove,
        append: consAppend,
    } = useFieldArray({
        control: methods.control,
        name: "playoffDates.consolation",
    });

    const onSubmit: SubmitHandler<ILeagueMgmtLeagueInfoData> = async data => {
        data.files = fileList;

        const leaguesPromise = isCreatingLeague
            ? createLeague(data, user.id)
            : updateLeague(leagueId, data, user.id);

        await leaguesPromise
            .then(response => {
                dispatch(leagueManagementActions.getLeagues());
                const alertAction = isCreatingLeague ? "created" : "updated";

                dispatch(
                    alertActions.success({
                        title: `League ${alertAction} successfully`,
                        dismissible: true,
                    }),
                );
            })
            .catch(e => {
                const alertAction = isCreatingLeague ? "creating" : "updating";

                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem ${alertAction} the league.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const onError: SubmitErrorHandler<
        ILeagueMgmtLeagueInfoData
    > = async data => {
        console.log(data);
    };

    const leagueTeams = league?.teams
        ? league.teams.map(t => {
              return {
                  value: t.id as number,
                  label: t.name as string,
              };
          })
        : undefined;

    const excludedTeams = league?.teams
        ? league.teams
              .filter(t => t.id && league.excludedTeams?.includes(t.id))
              .map(t => {
                  return {
                      value: t.id as number,
                      label: t.name as string,
                  };
              })
        : undefined;

    const isLeagueActive = isCreatingLeague
        ? methods.watch("active", true)
        : methods.watch("active", league?.active);

    const defaultAgeLevel = ageLevels.find(a => a.ageLevel === league?.ageLevel)
        ? {
              value: ageLevels.find(a => a.ageLevel === league?.ageLevel)
                  ?.id as IAgeLevel["id"],
              label: league?.ageLevel as IAgeLevel["ageLevel"],
          }
        : undefined;

    const defaultDesignation = designations.find(
        d => d.designation === league?.designation,
    )
        ? {
              value: designations.find(
                  d => d.designation === league?.designation,
              )?.id,
              label: league?.designation || undefined,
          }
        : undefined;

    const defaultDirection = directions.find(
        d => d.direction === league?.direction,
    )
        ? {
              value: directions.find(d => d.direction === league?.direction)
                  ?.id,
              label: league?.direction || undefined,
          }
        : undefined;

    const defaultSeason = seasons.seasons.find(s => s.season === league?.season)
        ? {
              value: seasons.seasons.find(s => s.season === league?.season)
                  ?.id as ISeason["id"],
              label: league?.season as ISeason["season"],
          }
        : undefined;

    const defaultBracket = brackets.brackets.find(
        b => parseInt(b.bracketId.toString()) === league?.playoffBracket?.id,
    )
        ? {
              value: league?.playoffBracket?.id as IPlayoffBracket["bracketId"],
              label: brackets.brackets.find(
                  b =>
                      parseInt(b.bracketId.toString()) ===
                      league?.playoffBracket?.id,
              )?.name as IPlayoffBracket["name"],
          }
        : undefined;

    const watchTeams = methods.watch("teams");

    useEffect(() => {
        const subscription = methods.watch((value, { name, type }) => {
            if (type === "change" && name === "bracket") {
                const newChampRounds = brackets.brackets.find(
                    b => b.bracketId === value.bracket?.value,
                )?.rounds.championship;

                const newConsRounds = brackets.brackets.find(
                    b => b.bracketId === value.bracket?.value,
                )?.rounds.consolation;

                if (newChampRounds) {
                    if (champRounds && newChampRounds < champRounds) {
                        for (let i = champRounds; i > newChampRounds; i -= 1) {
                            champRemove(i - 1);
                            methods.unregister(
                                `playoffDates.championship.${i - 1}`,
                            );
                        }
                    } else if (
                        (champRounds && newChampRounds > champRounds) ||
                        !champRounds
                    ) {
                        for (
                            let i = champRounds ? champRounds : 0;
                            i < newChampRounds;
                            i += 1
                        ) {
                            champAppend({
                                date: undefined,
                                round: i + 1,
                            });
                        }
                    }
                } else {
                    if (champRounds) {
                        champRemove();
                    }
                }

                if (newConsRounds) {
                    if (consRounds && newConsRounds < consRounds) {
                        for (let i = consRounds; i > newConsRounds; i -= 1) {
                            consRemove(i - 1);
                            methods.unregister(
                                `playoffDates.consolation.${i - 1}`,
                            );
                        }
                    } else if (
                        (consRounds && newConsRounds > consRounds) ||
                        !consRounds
                    ) {
                        for (
                            let i = consRounds ? consRounds : 0;
                            i < newConsRounds;
                            i += 1
                        ) {
                            consAppend({
                                date: undefined,
                                round: i + 1,
                            });
                        }
                    }
                } else {
                    if (consRounds) {
                        consRemove();
                    }
                }

                setChampRounds(newChampRounds);
                setConsRounds(newConsRounds);
            }
        });

        return () => subscription.unsubscribe();
    }, [methods.watch("bracket")]);

    useEffect(() => {
        const fetchTeams = async (
            ageLevel: string,
            year: number,
            season: string,
        ) => {
            await getTeamsByAgeLevel(ageLevel, year).then(result => {
                methods.setValue("teams", []);

                setTeams(
                    result?.teams
                        .sort((a, b) => {
                            return a.name.localeCompare(b.name);
                        })
                        .map(t => {
                            return {
                                value: t.id,
                                label: t.name,
                            };
                        }),
                );
            });
        };

        const subscription = methods.watch((value, { name, type }) => {
            if (type === "change") {
                switch (name) {
                    case "ageLevel":
                        fetchTeams(
                            value.ageLevel!.label!,
                            value.year!,
                            value.season!.label!,
                        );
                        break;
                    case "year":
                    case "season":
                        if (isCreatingLeague) {
                            methods.resetField("ageLevel");
                            methods.resetField("teams");
                            methods.setValue("ageLevel", {
                                value: 0,
                                label: "Select...",
                            });
                            methods.setValue("teams", []);
                            setTeams([]);
                        }
                        break;
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [
        methods.watch("ageLevel"),
        methods.watch("year"),
        methods.watch("season"),
    ]);

    return (
        <section className="leagueInfo">
            <FormProvider {...methods}>
                <Form
                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                    id="leagueManagementLeagueInfoForm"
                >
                    <Row>
                        <Col>
                            <Form.Label className="mt-0">
                                League Logo
                            </Form.Label>
                            <br />
                            {league?.logo && league?.logo !== "" && (
                                <img
                                    className="leagueLogo"
                                    src={`${LEAGUE_LOGO_URL}${league?.logo}`}
                                />
                            )}
                            <Form.Group controlId="files">
                                <FileUpload
                                    maxFileCount={1}
                                    allowedFileExtensions={allowedExtensions}
                                    maxFileSize={1024000}
                                    fileList={fileList}
                                    setFileList={setFileList}
                                />
                                <Form.Label className="small">
                                    Accepted file types: jpg, jpeg, png
                                    <br />
                                    Max file size: 1 MB
                                </Form.Label>
                                <p className="small mb-0">
                                    Upload a new logo to replace current one
                                </p>
                            </Form.Group>
                            {methods.formState.errors.files &&
                                displayFormAlert({
                                    level: "danger",
                                    text: methods.formState.errors.files
                                        .message as string,
                                })}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="year">
                                <Form.Label>League Year</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...methods.register("year", {
                                        value: league?.year,
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.year
                                            ? "true"
                                            : "false"
                                    }
                                />
                                {methods.formState.errors.year &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.year
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="season">
                                <Form.Label>League Season</Form.Label>
                                <Controller
                                    name="season"
                                    control={methods.control}
                                    defaultValue={defaultSeason}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            defaultValue={defaultSeason}
                                            options={seasons.seasons.map(s => {
                                                return {
                                                    value: parseInt(
                                                        s.id.toString(),
                                                    ),
                                                    label: s.season,
                                                };
                                            })}
                                        />
                                    )}
                                />
                                {methods.formState.errors.season &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.season
                                            ?.label?.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="ageLevel">
                                <Form.Label>Age Level</Form.Label>
                                <Controller
                                    name="ageLevel"
                                    control={methods.control}
                                    defaultValue={defaultAgeLevel}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            defaultValue={defaultAgeLevel}
                                            options={ageLevels.map(a => {
                                                return {
                                                    value: parseInt(
                                                        a.id.toString(),
                                                    ),
                                                    label: a.ageLevel,
                                                };
                                            })}
                                            isDisabled={
                                                isCreatingLeague &&
                                                (!methods.watch("year") ||
                                                    !methods.watch("season"))
                                            }
                                        />
                                    )}
                                />
                                {methods.formState.errors.ageLevel &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.ageLevel
                                            .label?.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="designation">
                                <Form.Label>Level Designation</Form.Label>
                                <Controller
                                    name="designation"
                                    control={methods.control}
                                    defaultValue={defaultDesignation}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            defaultValue={defaultDesignation}
                                            isClearable={true}
                                            required={false}
                                            options={designations.map(d => {
                                                return {
                                                    value: parseInt(
                                                        d.id.toString(),
                                                    ),
                                                    label: d.designation,
                                                };
                                            })}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="direction">
                                <Form.Label>Direction</Form.Label>
                                <Controller
                                    name="direction"
                                    control={methods.control}
                                    defaultValue={defaultDirection}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            defaultValue={defaultDirection}
                                            isClearable={true}
                                            required={false}
                                            options={directions.map(d => {
                                                return {
                                                    value: parseInt(
                                                        d.id.toString(),
                                                    ),
                                                    label: d.direction,
                                                };
                                            })}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group controlId="teams" as={Col}>
                            <Form.Label>
                                Teams (
                                {watchTeams
                                    ? watchTeams.length
                                    : league?.teams
                                      ? league?.teams.length
                                      : 0}
                                )
                            </Form.Label>
                            <Controller
                                name="teams"
                                control={methods.control}
                                defaultValue={leagueTeams}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        isSearchable={true}
                                        options={teams}
                                        defaultValue={leagueTeams}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group controlId="teams" as={Col}>
                            <Form.Label>Exclude Teams From Playoffs</Form.Label>
                            <Controller
                                name="excludedTeams"
                                control={methods.control}
                                defaultValue={excludedTeams}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        isSearchable={true}
                                        options={leagueTeams}
                                        defaultValue={excludedTeams}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="bracket">
                                <Form.Label>Bracket/Playoff Type</Form.Label>
                                <Controller
                                    name="bracket"
                                    control={methods.control}
                                    defaultValue={defaultBracket}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            isClearable={true}
                                            defaultValue={defaultBracket}
                                            options={brackets.brackets.map(
                                                b => {
                                                    return {
                                                        value: parseInt(
                                                            b.bracketId.toString(),
                                                        ),
                                                        label: b.name,
                                                    };
                                                },
                                            )}
                                        />
                                    )}
                                />
                                {methods.formState.errors.bracket &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.bracket
                                            .label?.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    {(champRounds || consRounds) && (
                        <Row>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Playoff Start Dates</Form.Label>
                                    {champRounds && (
                                        <Form.Group>
                                            <Form.Label className="mt-0">
                                                Championship Rounds
                                            </Form.Label>
                                            {champFields.map((d, idx) => {
                                                return (
                                                    <Form.Group
                                                        as={Row}
                                                        key={`champ-rd-${idx}`}
                                                    >
                                                        <Form.Control
                                                            type="hidden"
                                                            {...methods.register(
                                                                `playoffDates.championship.${idx}.round`,
                                                                {
                                                                    value:
                                                                        idx + 1,
                                                                },
                                                            )}
                                                        />
                                                        <Form.Label
                                                            className="mt-0"
                                                            column
                                                            xs={4}
                                                        >
                                                            Round {d.round}
                                                        </Form.Label>
                                                        <Col xs={8}>
                                                            <Controller
                                                                name={`playoffDates.championship.${idx}.date`}
                                                                render={({
                                                                    field: {
                                                                        value,
                                                                    },
                                                                }) => (
                                                                    <DatePicker
                                                                        {...methods.register(
                                                                            `playoffDates.championship.${idx}.date` as const,
                                                                        )}
                                                                        selected={
                                                                            value
                                                                        }
                                                                        onChange={date => {
                                                                            const championship =
                                                                                methods.getValues(
                                                                                    "playoffDates.championship",
                                                                                );
                                                                            if (
                                                                                championship &&
                                                                                !date
                                                                            ) {
                                                                                for (
                                                                                    let i =
                                                                                        idx;
                                                                                    i <
                                                                                    championship.length;
                                                                                    i++
                                                                                ) {
                                                                                    methods.setValue(
                                                                                        `playoffDates.championship.${i}`,
                                                                                        {
                                                                                            round:
                                                                                                i +
                                                                                                1,
                                                                                            date: null,
                                                                                        },
                                                                                        {
                                                                                            shouldValidate:
                                                                                                true,
                                                                                        },
                                                                                    );
                                                                                }
                                                                            } else {
                                                                                methods.setValue(
                                                                                    `playoffDates.championship.${idx}`,
                                                                                    {
                                                                                        round:
                                                                                            idx +
                                                                                            1,
                                                                                        date,
                                                                                    },
                                                                                    {
                                                                                        shouldValidate:
                                                                                            true,
                                                                                    },
                                                                                );
                                                                            }
                                                                        }}
                                                                        showMonthDropdown
                                                                        showYearDropdown
                                                                        dateFormat="MMMM d, yyyy"
                                                                        dropdownMode="select"
                                                                        className="form-control"
                                                                        minDate={
                                                                            idx !==
                                                                            0
                                                                                ? methods.getValues(
                                                                                      `playoffDates.championship.${idx - 1}.date`,
                                                                                  ) ||
                                                                                  undefined
                                                                                : undefined
                                                                        }
                                                                        isClearable
                                                                        disabled={
                                                                            idx >
                                                                            0
                                                                                ? methods
                                                                                      .getValues(
                                                                                          `playoffDates.championship`,
                                                                                      )
                                                                                      ?.some(
                                                                                          (
                                                                                              v,
                                                                                              i,
                                                                                          ) =>
                                                                                              i <
                                                                                                  idx &&
                                                                                              !v.date,
                                                                                      )
                                                                                : false
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        </Col>
                                                    </Form.Group>
                                                );
                                            })}
                                        </Form.Group>
                                    )}
                                    {consRounds && (
                                        <Form.Group>
                                            <Form.Label>
                                                Consolation Rounds
                                            </Form.Label>
                                            {consFields.map((d, idx) => {
                                                return (
                                                    <Form.Group
                                                        as={Row}
                                                        key={`cons-rd-${idx}`}
                                                    >
                                                        <Form.Control
                                                            type="hidden"
                                                            {...methods.register(
                                                                `playoffDates.consolation.${idx}.round`,
                                                                {
                                                                    value:
                                                                        idx + 1,
                                                                },
                                                            )}
                                                        />
                                                        <Form.Label
                                                            className="mt-0"
                                                            column
                                                            xs={4}
                                                        >
                                                            Round {d.round}
                                                        </Form.Label>
                                                        <Col xs={8}>
                                                            <Controller
                                                                name={`playoffDates.consolation.${idx}.date`}
                                                                control={
                                                                    methods.control
                                                                }
                                                                render={({
                                                                    field: {
                                                                        value,
                                                                    },
                                                                }) => (
                                                                    <DatePicker
                                                                        {...methods.register(
                                                                            `playoffDates.consolation.${idx}.date` as const,
                                                                        )}
                                                                        selected={
                                                                            value
                                                                        }
                                                                        onChange={date => {
                                                                            const consolation =
                                                                                methods.getValues(
                                                                                    "playoffDates.consolation",
                                                                                );
                                                                            if (
                                                                                consolation &&
                                                                                !date
                                                                            ) {
                                                                                for (
                                                                                    let i =
                                                                                        idx;
                                                                                    i <
                                                                                    consolation.length;
                                                                                    i++
                                                                                ) {
                                                                                    methods.setValue(
                                                                                        `playoffDates.consolation.${i}`,
                                                                                        {
                                                                                            round:
                                                                                                i +
                                                                                                1,
                                                                                            date: null,
                                                                                        },
                                                                                        {
                                                                                            shouldValidate:
                                                                                                true,
                                                                                        },
                                                                                    );
                                                                                }
                                                                            } else {
                                                                                methods.setValue(
                                                                                    `playoffDates.consolation.${idx}`,
                                                                                    {
                                                                                        round:
                                                                                            idx +
                                                                                            1,
                                                                                        date,
                                                                                    },
                                                                                    {
                                                                                        shouldValidate:
                                                                                            true,
                                                                                    },
                                                                                );
                                                                            }
                                                                        }}
                                                                        showMonthDropdown
                                                                        showYearDropdown
                                                                        dateFormat="MMMM d, yyyy"
                                                                        dropdownMode="select"
                                                                        className="form-control"
                                                                        minDate={
                                                                            idx !==
                                                                            0
                                                                                ? methods.getValues(
                                                                                      `playoffDates.consolation.${idx - 1}.date`,
                                                                                  ) ||
                                                                                  undefined
                                                                                : undefined
                                                                        }
                                                                        isClearable
                                                                        disabled={
                                                                            idx >
                                                                            0
                                                                                ? methods
                                                                                      .getValues(
                                                                                          `playoffDates.consolation`,
                                                                                      )
                                                                                      ?.some(
                                                                                          (
                                                                                              v,
                                                                                              i,
                                                                                          ) =>
                                                                                              i <
                                                                                                  idx &&
                                                                                              !v.date,
                                                                                      )
                                                                                : false
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        </Col>
                                                    </Form.Group>
                                                );
                                            })}
                                        </Form.Group>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col>
                            <Form.Group
                                controlId="active"
                                as={Col}
                                md={4}
                                className="col"
                            >
                                <Form.Control
                                    type="hidden"
                                    {...methods.register("active", {
                                        value: isCreatingLeague
                                            ? true
                                            : methods.getValues("active") ||
                                              Boolean(
                                                  league?.active.toString(),
                                              ),
                                    })}
                                />
                                <ButtonGroup>
                                    <Button
                                        variant={
                                            isLeagueActive
                                                ? "primary"
                                                : "outline-primary"
                                        }
                                        onClick={() =>
                                            methods.setValue("active", true)
                                        }
                                    >
                                        Active
                                    </Button>
                                    <Button
                                        variant={
                                            isLeagueActive
                                                ? "outline-primary"
                                                : "primary"
                                        }
                                        onClick={() =>
                                            methods.setValue("active", false)
                                        }
                                    >
                                        Inactive
                                    </Button>
                                </ButtonGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="primary" type="submit">
                                {isCreatingLeague
                                    ? "Add League"
                                    : "Update Information"}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
        </section>
    );
};

export default League;
